import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import i18n from "../../translationUtils/i18n";

const BreadcrumbTrail = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const wrapperRef = useRef(null);
  const trailRef = useRef(null);
  const [fits, setFits] = useState(true);

  const breadCrumbtextStyle = {
    color: "text.primary",
    underline: "hover",
  };

  const getPathParts = () => {
    const { pathname } = location;
    return pathname.split("/").filter((part) => part !== "");
  };

  const handleClick = (event, path) => {
    event.preventDefault();
    navigate(path);
  };

  const pathParts = getPathParts();

  const measure = useCallback(() => {
    const wrapper = wrapperRef.current;
    const trail = trailRef.current;
    if (!wrapper || !trail) return;
    const available = wrapper.clientWidth;
    const needed = trail.scrollWidth;
    setFits(needed <= available + 1);
  }, []);

  useLayoutEffect(() => {
    measure();
    const wrapper = wrapperRef.current;
    if (wrapper && typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(measure);
      ro.observe(wrapper);
      window.addEventListener("resize", measure);
      return () => {
        ro.disconnect();
        window.removeEventListener("resize", measure);
      };
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure, location.pathname]);

  return (
    <Box
      ref={wrapperRef}
      sx={{
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        pl: { xs: 1, sm: 2, md: "30px" },
      }}
    >
      <Breadcrumbs
        ref={trailRef}
        aria-hidden={!fits}
        sx={{
          flexShrink: 0,
          whiteSpace: "nowrap",
          visibility: fits ? "visible" : "hidden",
          fontSize: { xs: "0.8rem", sm: "0.95rem", md: "1.1rem" },
          "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap" },
          "& .MuiBreadcrumbs-li": {
            fontSize: "inherit",
            whiteSpace: "nowrap",
          },
          "& .MuiBreadcrumbs-li a, & .MuiBreadcrumbs-li p": {
            fontSize: "inherit",
          },
          "& .MuiBreadcrumbs-separator": {
            mx: { xs: 0.5, sm: 1 },
          },
        }}
      >
        {pathParts.length > 0 && (
          <Link component={RouterLink} to="/" color="inherit" underline="hover">
            {i18n.t("healthdashboard")}
          </Link>
        )}
        {pathParts.map((part, index) => {
          const path = `/${pathParts.slice(0, index + 1).join("/")}`;
          const title = part.charAt(0).toUpperCase() + part.slice(1);
          const isLast = index === pathParts.length - 1;

          return isLast ? (
            <Link key={index} {...breadCrumbtextStyle}>
              {i18n.t(title.toLowerCase())}
            </Link>
          ) : (
            <Link
              key={index}
              component={RouterLink}
              to={path}
              onClick={(event) => handleClick(event, path)}
              {...breadCrumbtextStyle}
            >
              {i18n.t(title.toLowerCase())}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

export default BreadcrumbTrail;
